<!-- eslint-disable vue/no-v-html -->
<template>
  <UtdModalDialog ref="modalRef"
                  v-bind="modalProps"
                  :modal-classes="`login-register-modal ${props.modalProps.modalClasses ?? ''}`"
                  @utd-modal-closing="close">
    <template #header>
      <div class="login-register-title">
        {{ props.title }}
      </div>
    </template>
    <div class="login-register-modal__container">
      <div class="login-register-modal__overlay-container">
        <div class="login-register-modal__overlay-content">
          <p>
            {{ props.welcomeMessage ?? defaultWelcomeMessage }}
          </p>
          <ul class="login-register-modal__message-array">
            <li v-for="(message, index) in props.messageArray ?? defaultMessageArray"
                :key="index"
                class="login-register-modal__message-item"
                v-html="message" />
          </ul>
        </div>
        <div class="login-register-modal__button-container">
          <UtdButton v-if="showSubscribe"
                     class="login-register-modal__button subscribe-button"
                     button-size="large"
                     button-color="green-shade-3"
                     @click="subscribe">{{ $t("HEADER.SUBSCRIBE") }}
          </UtdButton>
          <UtdButton v-if="showRegister"
                     class="login-register-modal__button subscribe-button"
                     button-size="large"
                     button-color="green-shade-3"
                     @click="register">{{ $t("LOGIN.REGISTER") }}
          </UtdButton>
          <UtdButton v-if="showLogin"
                     class="login-register-modal__button login-button"
                     button-size="large"
                     @click="login">{{ $t("LOGIN.LOG_IN") }}
          </UtdButton>
        </div>
      </div>
    </div>
  </UtdModalDialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { OIDC_WELCOME_MESSAGES } from '_acaSrc/utility/constants';
import { getWindow } from '_acaSrc/utility/DOM';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import UtdCache from '_acaSrc/utility/UtdCache';

const props = defineProps({
    modalProps: { type: Object, default: () => ({}) },
    title: { type: String, default: 'UpToDate® - digital answers at your fingertips' },
    welcomeMessage: String,
    messageArray: Array,
    showSubscribe: { type: Boolean, default: false },
    showRegister: { type: Boolean, default: true },
    showLogin: { type: Boolean, default: true }
});

const store = useStore();
const router = computed(() => store.getters['app/router']);
const isAuthenticatedByIp = computed(() => store.getters['app/isAuthenticatedByIp']);
const storeUrl = computed(() => store.getters['auth/storeUrl']);

const defaultWelcomeName = computed(() => store.getters['profile/welcomeName']);
const defaultWelcomeMessage = computed(() => {
    return defaultWelcomeName?.value
        ? `Thanks for accessing UpToDate® through ${defaultWelcomeName.value}.`
        : 'Thanks for accessing UpToDate®.';
});
const defaultMessageArray = computed(() => OIDC_WELCOME_MESSAGES.REGISTER);
const modalRef = ref(null);

const open = () => {
    modalRef.value.open();
};

const close = () => {
    modalRef.value.close();
};

const login = () => {
    // save URL that the user is currently at in order to redirect them here after SSO login
    const relativeUrl = window.location.pathname + window.location.search;
    new UtdCache().setPersistent('ssoRedirectURL', relativeUrl, 1);
    // historically, it appears there was a doximity bug relating to login via route change
    // this behavior was carried over from UtdPartnerOidcModal in case there is still an issue
    if (isAuthenticatedByIp.value) {
        router.value.go('login');
        close(); // close modal when using router
    }
    else {
        getWindow().location.href = '/login';
    }
};

const register = () => {
    router.value.go('loginRegister');
    close(); // close modal when using router
};

const subscribe = () => {
    getWindow().open(storeUrl.value, 'storefront', 'noopener');
};

defineExpose({ open, close });

</script>

<style lang="less">
@import "~_acaAssets/login/login-register-modal";
</style>